import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useOmniDataSheetDeleteMutation } from "../api/data-sheet-api";
import { DataSheet } from "../types/data-sheet";

/**
 * DataSheetDeleteDialog deletes the data sheet and its file. The backend
 * refuses while an option or a position references it.
 */
export function DataSheetDeleteDialog({ dataSheet }: { dataSheet: DataSheet }) {
  const [open, setOpen] = useState(false);
  const [deleteDataSheet, { isLoading, error, reset }] =
    useOmniDataSheetDeleteMutation();

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      reset();
    }
  };

  const onConfirm = () => {
    if (isLoading) {
      return;
    }
    deleteDataSheet({ id: dataSheet.id })
      .unwrap()
      .then(() => {
        toast.success(t("Datenblatt gelöscht"));
        onOpenChange(false);
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <CommonDialogTrigger icon={Trash2} label="" variant="destructive" />
      <DialogContent>
        <CommonDialogHeader
          title={t("Datenblatt wirklich löschen?")}
          description={t(
            "Das Datenblatt und seine Datei werden gelöscht. Solange eine Position oder Option darauf verweist, ist das nicht möglich; setzen Sie es dann inaktiv.",
          )}
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Löschen")}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}
